import React, { useState, useEffect, useRef } from "react";
import { Icon } from "./Icon";

interface PersonalitySelectorProps {
  currentPersonality: string;
  currentCustomContext?: string;
  onUpdate: (
    personality: string,
    customContext?: string,
  ) => void | Promise<void>;
  token: string;
  effectiveUserType?: "user" | "premium" | "vip" | "admin";
}

const personalities = [
  { value: "helper", label: "Helper", description: "Helpful and informative" },
  { value: "friendly", label: "Friendly", description: "Warm and casual" },
  { value: "witty", label: "Witty", description: "Clever and funny" },
  {
    value: "professional",
    label: "Professional",
    description: "Formal and accurate",
  },
  { value: "gamer", label: "Gamer", description: "Gaming enthusiast" },
  {
    value: "sarcastic",
    label: "Sarcastic",
    description: "Playfully sarcastic",
  },
  { value: "educational", label: "Educational", description: "Teacher mode" },
  {
    value: "motivational",
    label: "Motivational",
    description: "Encouraging and positive",
  },
  { value: "chill", label: "Chill", description: "Relaxed and laid-back" },
  { value: "hype", label: "Hype", description: "Maximum energy!" },
  { value: "dj", label: "DJ", description: "Drops beats and mixing knowledge" },
  {
    value: "musician",
    label: "Musician",
    description: "Music theory and performance tips",
  },
  {
    value: "synthnerd",
    label: "Synth Nerd",
    description: "Analog obsessed, patch cable guru",
  },
  {
    value: "hal8580",
    label: "HAL 8580",
    description: "I'm afraid I can't do that, Dave",
  },
  {
    value: "haldark",
    label: "HAL DARK",
    description: "Dark humor HAL with sarcasm",
  },
];

export const PersonalitySelector: React.FC<PersonalitySelectorProps> = ({
  currentPersonality,
  currentCustomContext,
  onUpdate,
  token,
  effectiveUserType,
}) => {
  const [selectedPersonality, setSelectedPersonality] =
    useState(currentPersonality);
  const [customContext, setCustomContext] = useState(
    currentCustomContext || "",
  );
  const [showCustom, setShowCustom] = useState(!!currentCustomContext);
  const [allowedPersonalities, setAllowedPersonalities] = useState<string[]>(
    [],
  );
  const [userType, setUserType] = useState<string>(effectiveUserType || "user");
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setSelectedPersonality(currentPersonality);
    setCustomContext(currentCustomContext || "");
    setShowCustom(!!currentCustomContext);
  }, [currentPersonality, currentCustomContext]);

  // Hide success message when user makes changes
  useEffect(() => {
    if (
      selectedPersonality !== currentPersonality ||
      customContext.trim() !== (currentCustomContext || "").trim()
    ) {
      setShowSuccess(false);
    }
  }, [
    selectedPersonality,
    customContext,
    currentPersonality,
    currentCustomContext,
  ]);

  useEffect(() => {
    // If effectiveUserType is provided, use it
    if (effectiveUserType) {
      setUserType(effectiveUserType);
      // For admin/premium/vip tier test, allow all personalities
      if (
        effectiveUserType === "admin" ||
        effectiveUserType === "premium" ||
        effectiveUserType === "vip"
      ) {
        setAllowedPersonalities(personalities.map((p) => p.value));
      } else {
        setAllowedPersonalities(["hal8580", "haldark"]);
      }
    } else {
      // Otherwise fetch allowed personalities for current user
      fetch("/api/bot/personalities/allowed", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch");
          return res.json();
        })
        .then((data) => {
          if (data && data.allowedPersonalities) {
            setAllowedPersonalities(data.allowedPersonalities);
            setUserType(data.userType || "user");
          }
        })
        .catch((err) => {
          console.error("Failed to fetch allowed personalities:", err);
          // Set default allowed personalities for basic users
          setAllowedPersonalities(["hal8580", "haldark"]);
        });
    }
  }, [token, effectiveUserType]);

  const handleUpdate = async () => {
    setIsSaving(true);
    try {
      // Pass empty string if custom context is empty (not undefined)
      // This ensures the backend can properly clear the custom context
      await onUpdate(
        selectedPersonality,
        showCustom ? customContext : undefined,
      );
      setShowSuccess(true);
      // Keep the success message visible for at least 5 seconds
      setTimeout(() => {
        // Only hide if values haven't changed
        setShowSuccess((current) => {
          return current;
        });
      }, 5000);
    } catch (error) {
      console.error("Failed to update personality:", error);
      setShowSuccess(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#2d3748",
        borderRadius: "8px",
        padding: "20px",
        marginTop: "20px",
        border: "1px solid #4a5568",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <h3 style={{ color: "#e2e8f0", marginTop: 0 }}>Bot Personality</h3>

      <div style={{ marginBottom: "20px" }}>
        <label
          style={{ display: "block", marginBottom: "10px", color: "#cbd5e0" }}
        >
          Select Personality:
        </label>
        <div style={{ position: "relative" }}>
          <select
            value={selectedPersonality}
            onChange={(e) => setSelectedPersonality(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 40px 10px 10px",
              backgroundColor: "#1a202c",
              color: "#e2e8f0",
              border: "1px solid #4a5568",
              borderRadius: "4px",
              fontSize: "16px",
              outline: "none",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
            }}
          >
            {personalities.map((p) => {
              const isAllowed =
                allowedPersonalities.length > 0
                  ? allowedPersonalities.includes(p.value)
                  : true;
              const isPremiumRequired =
                !["hal8580", "haldark"].includes(p.value) &&
                userType === "user";
              return (
                <option key={p.value} value={p.value} disabled={!isAllowed}>
                  {p.label} - {p.description}{" "}
                  {isPremiumRequired ? "(Premium)" : ""}
                </option>
              );
            })}
          </select>
          <div
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              color: "#cbd5e0",
              fontSize: "14px",
            }}
          >
            ▼
          </div>
        </div>
      </div>

      {allowedPersonalities.length > 0 &&
        !allowedPersonalities.includes(selectedPersonality) &&
        userType === "user" && (
          <div
            style={{
              marginBottom: "20px",
              padding: "10px",
              backgroundColor: "#553c7b",
              borderRadius: "4px",
              fontSize: "13px",
              color: "#cbd5e0",
            }}
          >
            <Icon
              name="lock"
              size={13}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            This personality requires a premium or admin account
          </div>
        )}

      {(userType === "premium" ||
        userType === "vip" ||
        userType === "admin") && (
        <div style={{ marginBottom: "20px" }}>
          <button
            onClick={() => setShowCustom(!showCustom)}
            style={{
              backgroundColor: "transparent",
              color: "#9146FF",
              border: "none",
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "14px",
              textDecoration: "none",
              borderRadius: "4px",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#4a5568")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            {showCustom ? "Hide" : "Add"} Custom Context
          </button>
        </div>
      )}

      {userType === "user" && (
        <div
          style={{
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "#2d3748",
            borderRadius: "4px",
            fontSize: "13px",
            color: "#cbd5e0",
          }}
        >
          <Icon
            name="lock"
            size={13}
            style={{ verticalAlign: "-2px", marginRight: "6px" }}
          />
          Custom Context is a premium feature
        </div>
      )}

      {showCustom && (
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{ display: "block", marginBottom: "10px", color: "#cbd5e0" }}
          >
            Custom Context (optional):
          </label>
          <p
            style={{
              fontSize: "12px",
              color: "#718096",
              marginBottom: "8px",
              marginTop: "-4px",
            }}
          >
            This adds additional instructions to your selected personality, not
            replaces it.
          </p>
          <textarea
            value={customContext}
            onChange={(e) => {
              if (e.target.value.length <= 2000) {
                setCustomContext(e.target.value);
              }
            }}
            placeholder="Add specific instructions for your bot. For example: 'You love talking about speedruns and always mention your favorite game is Mario 64'"
            maxLength={2000}
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#1a202c",
              color: "#e2e8f0",
              border: "1px solid #4a5568",
              borderRadius: "4px",
              fontSize: "14px",
              minHeight: "100px",
              resize: "vertical",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          <div
            style={{
              fontSize: "12px",
              color: customContext.length > 1800 ? "#F59E0B" : "#718096",
              marginTop: "4px",
              textAlign: "right",
            }}
          >
            {customContext.length}/2000 characters
          </div>
        </div>
      )}

      {showSuccess && (
        <div
          style={{
            marginBottom: "12px",
            padding: "10px 14px",
            backgroundColor: "rgba(72, 187, 120, 0.15)",
            border: "1px solid rgba(72, 187, 120, 0.5)",
            borderRadius: "4px",
            color: "#68D391",
            fontSize: "14px",
            fontWeight: "normal",
            textAlign: "center",
          }}
        >
          <Icon
            name="check"
            size={14}
            color="#10B981"
            style={{ verticalAlign: "-2px", marginRight: "6px" }}
          />
          Personality updated
        </div>
      )}

      <button
        onClick={handleUpdate}
        disabled={
          isSaving ||
          (selectedPersonality === currentPersonality &&
            customContext.trim() === (currentCustomContext || "").trim()) ||
          (allowedPersonalities.length > 0 &&
            !allowedPersonalities.includes(selectedPersonality))
        }
        style={{
          backgroundColor: "#9146FF",
          color: "white",
          border: "none",
          padding: "10px 20px",
          fontSize: "16px",
          borderRadius: "4px",
          cursor:
            isSaving ||
            (selectedPersonality === currentPersonality &&
              customContext.trim() === (currentCustomContext || "").trim()) ||
            (allowedPersonalities.length > 0 &&
              !allowedPersonalities.includes(selectedPersonality))
              ? "not-allowed"
              : "pointer",
          opacity:
            isSaving ||
            (selectedPersonality === currentPersonality &&
              customContext.trim() === (currentCustomContext || "").trim()) ||
            (allowedPersonalities.length > 0 &&
              !allowedPersonalities.includes(selectedPersonality))
              ? 0.5
              : 1,
          width: "100%",
          transition: "all 0.3s ease",
        }}
      >
        {isSaving ? "Saving..." : "Update Personality"}
      </button>
    </div>
  );
};
